/**
 * ctz-benefits-bar-carousel.js
 *
 * Measures whether CTZ Benefits Bar's row of badges genuinely fits on
 * one line, and switches to a carousel the moment it doesn't -- rather
 * than guessing a fixed pixel breakpoint, since the actual "too small"
 * point depends entirely on how many badges exist and how long their
 * text is.
 *
 * Two carousel styles depending on viewport:
 *   - Mobile (max-width: 749px): one badge at a time, absolutely
 *     positioned, cross-fading via .is-carousel/.is-active. Unchanged
 *     from before.
 *   - Non-mobile (750px+): at least 2 badges shown together at once, as
 *     a normal flex row with the rest hidden via display:none, and the
 *     visible window sliding forward by one badge each tick. display
 *     :none (not opacity) is what lets the visible items' existing
 *     flex: 1 1 0 grow to fill the row properly, but that also means
 *     this swap is an instant cut rather than a cross-fade -- display
 *     can't be transitioned, and animating it any other way (e.g.
 *     absolutely positioning every possible group) would give up that
 *     fill-the-row behavior.
 *
 * Measurement technique: the section's CSS forces the badge row onto a
 * single line (flex-wrap: nowrap, white-space: nowrap on the heading/
 * subtext) whenever it is NOT in single-item carousel mode -- so a
 * genuine mismatch between content and available width shows up as real
 * horizontal overflow (scrollWidth > clientWidth) rather than silent
 * text-wrapping. That overflow check is the core signal throughout. For
 * a candidate group size, every possible rotation is checked (not just
 * the first one), since a later window can contain longer badge text
 * than the one initially tested and overflow only once it rotates in.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  function initOne(sectionId) {
    var section = document.getElementById(sectionId);
    if (!section) return;

    var container = section.querySelector('.trust-badges');
    var items = Array.prototype.slice.call(section.querySelectorAll('.trust-badges__item'));
    if (!container || items.length === 0) return;
    if (container.dataset.ctzBenefitsInit === 'true') return;
    container.dataset.ctzBenefitsInit = 'true';

    var n = items.length;

    if (n === 1) {
      items[0].classList.add('is-active');
      return; // nothing to fit-check or carousel with a single badge
    }

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var intervalMs = reducedMotion ? 6000 : 3500;

    var mode = 'all'; // 'all' | 'single' | 'group'
    var current = 0; // rotation position; meaning depends on mode
    var groupSize = n; // only meaningful in 'group' mode
    var timer = null;

    function isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    function stopTimer() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    function startTimer(advanceFn) {
      stopTimer();
      timer = setInterval(advanceFn, intervalMs);
    }

    // ---- Measurement --------------------------------------------------------

    // Hides every item except the window [start, start + k) (mod n),
    // forces a layout read, and reports whether the row overflows.
    // Always measured with .is-carousel off, since its absolute
    // positioning wouldn't produce a meaningful scrollWidth.
    function windowFits(start, k) {
      var i;
      for (i = 0; i < n; i++) {
        items[i].style.display = 'none';
      }
      for (i = 0; i < k; i++) {
        items[(start + i) % n].style.display = '';
      }
      // eslint-disable-next-line no-unused-expressions
      container.offsetHeight; // force reflow before reading scrollWidth
      return container.scrollWidth <= container.clientWidth + 1;
    }

    // A group size only counts as usable if EVERY rotation of that size
    // fits -- checking just one window isn't enough, since a later
    // window could contain longer text and overflow once it rotates in.
    function allWindowsFit(k) {
      for (var start = 0; start < n; start++) {
        if (!windowFits(start, k)) return false;
      }
      return true;
    }

    function resetItemOverrides() {
      items.forEach(function (item) {
        item.style.display = '';
        item.style.borderLeft = '';
      });
    }

    // ---- Mode transitions -----------------------------------------------------

    function showAll() {
      mode = 'all';
      stopTimer();
      container.classList.remove('is-carousel');
      items.forEach(function (item) {
        item.classList.remove('is-active');
      });
      resetItemOverrides();
    }

    function enterSingle() {
      mode = 'single';
      current = 0;
      resetItemOverrides();
      container.classList.add('is-carousel');
      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === current);
      });
      startTimer(advanceSingle);
    }

    function advanceSingle() {
      items[current].classList.remove('is-active');
      current = (current + 1) % n;
      items[current].classList.add('is-active');
    }

    function enterGroup(size) {
      mode = 'group';
      groupSize = size;
      current = 0;
      container.classList.remove('is-carousel');
      items.forEach(function (item) {
        item.classList.remove('is-active');
      });
      applyGroupWindow();
      startTimer(advanceGroup);
    }

    function applyGroupWindow() {
      for (var i = 0; i < n; i++) {
        var inWindow = false;
        for (var j = 0; j < groupSize; j++) {
          if ((current + j) % n === i) {
            inWindow = true;
            break;
          }
        }
        items[i].style.display = inWindow ? '' : 'none';
        // The stylesheet's :first-child border removal only recognizes
        // the literal first DOM element -- if that one is hidden this
        // rotation, whichever visible item now sits first would
        // otherwise show its own orphaned leading border. Handled here
        // instead: clear border-left specifically on the window's own
        // first item (i === current), and restore the stylesheet's rule
        // everywhere else so the dividers between visible badges still
        // render normally.
        items[i].style.borderLeft = (inWindow && i === current) ? 'none' : '';
      }
    }

    function advanceGroup() {
      current = (current + 1) % n;
      applyGroupWindow();
    }

    // ---- Deciding what to show ------------------------------------------------

    function checkFit() {
      stopTimer();
      container.classList.remove('is-carousel');
      resetItemOverrides();

      if (allWindowsFit(n)) {
        showAll();
        return;
      }

      if (isMobile()) {
        resetItemOverrides();
        enterSingle();
        return;
      }

      // Non-mobile: find the largest group size (>= 2) where every
      // rotation fits. Falls back to a floor of 2 even if that doesn't
      // perfectly fit in some unusually tight edge case -- "at least 2"
      // takes priority over a guaranteed fit.
      var size = n - 1;
      while (size > 2 && !allWindowsFit(size)) {
        size -= 1;
      }

      resetItemOverrides();
      enterGroup(size);
    }

    checkFit();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkFit, 150);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopTimer();
      } else if (mode === 'single') {
        startTimer(advanceSingle);
      } else if (mode === 'group') {
        startTimer(advanceGroup);
      }
    });
  }

  window.CTZBenefitsBarCarousel = {
    init: initOne
  };
})();